/**
 * ApplicationCell
 *
 * Renders the parent Application that this :Asset observation rolls up
 * under. Prefers the parent FQDN resolved via HAS_MEMBER on the row
 * (`asset.application.fqdn`) over the denormalized `asset.group` field,
 * since `group` is reroute-fragile once subdomains route under a root.
 * See rollup-frontend-plan.md item A.
 *
 * Click does not navigate — the table row click already opens the unified
 * Application drawer (via `handleOpenAssetDrawer`'s resolve-up logic).
 */
import React from 'react';

import { Chip } from '@/components/Chip';
import { assetType } from '@/sections/asset/utils';
import type { Asset } from '@/types';

interface ApplicationCellProps {
  asset: Asset;
}

export const ApplicationCell: React.FC<ApplicationCellProps> = ({ asset }) => {
  // Application rows themselves: don't show a parent chip — the identifier
  // column already renders their FQDN.
  if (assetType(asset) === 'application') {
    return <span className="text-default-light">-</span>;
  }

  // Prefer the HAS_MEMBER-resolved parent (reroute-safe). Fall back to
  // `asset.group` only when the enrichment query hasn't landed yet.
  const parentFqdn = asset.application?.fqdn || asset.group;
  if (!parentFqdn || parentFqdn === asset.identifier) {
    return <span className="text-default-light">-</span>;
  }
  return (
    <Chip style="secondary" title={parentFqdn} className="max-w-full">
      {parentFqdn}
    </Chip>
  );
};
